/**
 * Ultron.js
 * The Ultron Enemy class
 */

import { Game } from "pixi_core";
import { Enemies, EnemyAbilities, GameEvents, NB_MAX_ENEMIES } from "../Constants";
import { Enemy } from "./Enemy";
import { Common } from "../game/Common";

export class Ultron extends Enemy {
    /**
     * @public
     * @param {String} enemyName
     * @param {Object} enemyData
     * @param {Number} index
     * @param {Boolean} [summoned] - Summoned by an ability
     */
    constructor(enemyName, enemyData, index, summoned) {
        super(enemyName, enemyData, index, summoned);

        /** @private @type {Number} - Probability to use the Concussive Beam ability */
        this._probConcussiveBeam = Game.utils.assertNumber(enemyData.ProbConcussiveBeam, 1);
        /** @private @type {Number} - Probability to use the Hacker ability */
        this._probHacker = Game.utils.assertNumber(enemyData.ProbHacker, 1);

        // Ability choices
        this._abilityChoices.addChoice(EnemyAbilities.eCONCUSSIVE_BEAM, this._probConcussiveBeam);
        this._abilityChoices.addChoice(EnemyAbilities.eHACKER, this._probHacker);

        /** @private @type {Enemies[]} - Which enemies can be added by the Troops Drop */
        this._ultronAdds = [Enemies.eULTRONS_SENTRIES, Enemies.eHEAVY_ULTRONS_SENTRIES, Enemies.eSUPPORT_ULTRONS_SENTRIES];

        /** @private @type {Number} - Amount of HP regenerated by the Upgrade ability */
        this._regenHP = Game.utils.assertNumber(enemyData.RegenHP, 1);

        /** @private @type {Number} - Number of weaknesses after the Upgrade ability */
        this._upgradeWeaknesses = Game.utils.assertNumber(enemyData.UpgradeWeaknesses, 1);

        /** @private @type {Number} - Probability to use Mass Production */
        this._probMassProduction = Game.utils.assertNumber(enemyData.ProbMassProduction, 1);

        /** @private @type {Number} - Probability to use Mass Production saved for variable probabilities */
        this._probMassProductionSaved = Game.utils.assertNumber(enemyData.ProbMassProduction, 1);

        /** @private @type {Boolean} - Has used the Upgrade ability */
        this._upgraded = false;
    }

    /**
     * Choose the next action in this enemy's attack phase
     * @protected
     * @override
     */
    _chooseNextAction() {
        if (this._nbActionsPerformed < this._nbActionsPerTurn && this._hp > 0) {
            ++this._nbActionsPerformed;

            if (this._currentAttackTimer > 0) {
                this._regularAttack();
            } else {
                let ability = this._chooseAbility();
                if (!this._upgraded && this._hp <= (this._maxHp * this._triggerHealthPercent) / 100) {
                    this._upgraded = true;
                    ability = EnemyAbilities.eUPGRADE;
                } else {
                    // GDD: [80%] chance to use mass production skill if there is an empty slot in the enemy team
                    const response = {};
                    Game.fire(GameEvents.eREQUEST_ACTIVE_ENEMIES, response);
                    let probMassProduction = Math.random();

                    if (probMassProduction < this._probMassProduction / 100 && response.activeEnemies.length < NB_MAX_ENEMIES) {
                        ability = EnemyAbilities.eMASS_PRODUCTION;
                    }
                }

                this._beginAbility(ability);
            }
        } else {
            this._onAttackPhaseCompleted();
        }
    }

    /**
     * Performs the regular/normal attack on heroes
     * @protected
     * @override
     */
    _regularAttack() {
        super._regularAttack();
    }

    /**
     * @protected
     * @override
     * @param {EnemyAbilities} abilityName
     * @param {{x: Number, y: Number}} startPos
     */
    _useAbility(abilityName, startPos) {
        switch (abilityName) {
            case EnemyAbilities.eHACKER:
                Game.fire(GameEvents.eGAME_ABILITY_HACKER, this._colorChangeBubbles, startPos, this._onAbilityCompleted.bind(this));
                break;

            case EnemyAbilities.eCONCUSSIVE_BEAM:
                // ADD_OVERTIME + JAM
                Game.fire(
                    GameEvents.eGAME_ABILITY_ADD_OVERTIME,
                    this._addOvertimeBubbles,
                    this._continueConcussiveBeam.bind(this, startPos)
                );
                break;

            case EnemyAbilities.eMASS_PRODUCTION:
                let randomAdd = Math.floor(Math.random() * this._ultronAdds.length);
                Game.fire(GameEvents.eGAME_ABILITY_MASS_PRODUCTION, this._ultronAdds[randomAdd], this._onAbilityCompleted.bind(this));
                break;

            case EnemyAbilities.eUPGRADE:
                Game.fire(GameEvents.eGAME_ABILITY_UPGRADE_HEAL, this, this._regenHP, this._continueUpgrade.bind(this));
                break;

            default:
                console.error("[Ultron._useAbility] Invalid ability name : " + abilityName);
                this._chooseNextAction();
        }
    }

    /**
     * @private
     * @param {{x: Number, y: Number}} startPos
     */
    _continueConcussiveBeam(startPos) {
        if (this._timerMultipleAbilityDelay) {
            this._timerMultipleAbilityDelay.destroy();
            this._timerMultipleAbilityDelay = null;
        }

        this._timerMultipleAbilityDelay = Game.windows.getCurrentWindow().createTimer(Common.settings.multipleAbilityDelay);
        this._timerMultipleAbilityDelay.onComplete = () => {
            Game.fire(GameEvents.eGAME_ABILITY_JAM, this._stunNumber, this._stunTurns, startPos, this._onAbilityCompleted.bind(this));
        };
    }

    /**
     * @private
     */
    _continueUpgrade() {
        if (this._timerMultipleAbilityDelay) {
            this._timerMultipleAbilityDelay.destroy();
            this._timerMultipleAbilityDelay = null;
        }

        this._timerMultipleAbilityDelay = Game.windows.getCurrentWindow().createTimer(Common.settings.multipleAbilityDelay);
        this._timerMultipleAbilityDelay.onComplete = () => {
            Game.fire(GameEvents.eGAME_ABILITY_UPGRADE_WEAKNESSES, this, this._onAbilityCompleted.bind(this));
        };
    }
}
